<!-- src/apps/session/components/PasswordStrengthMeter.vue -->

<script setup lang="ts">
  /**
   * Password strength meter for the account password (reset / choose-a-new).
   *
   * BACKEND TRUTH (honest copy):
   * The Rodauth backend enforces exactly ONE account-password rule —
   * `password_minimum_length 8` (apps/web/auth/config/features/account_management.rb),
   * with `login_password_requirements_base` enabled but no complexity rule. So
   * the minimum length is the only hard *requirement* we surface; character
   * variety is shown as strength *recommendations*, never as a gate. We never
   * block submit here — the backend remains the authority; this is visual
   * feedback only.
   *
   * All text names font-mono/font-sans explicitly: the base layer falls back to
   * ui-serif (Georgia) for any surface that doesn't name a family.
   */
  import { useI18n } from 'vue-i18n';
  import { computed } from 'vue';

  const props = withDefaults(
    defineProps<{
      /** The password value being scored */
      password: string;
      /** Backend-enforced minimum length (Rodauth password_minimum_length) */
      minLength?: number;
    }>(),
    { minLength: 8 }
  );

  const { t } = useI18n();

  const len = computed(() => props.password.length);
  const hasLower = computed(() => /[a-z]/.test(props.password));
  const hasUpper = computed(() => /[A-Z]/.test(props.password));
  const hasNumber = computed(() => /\d/.test(props.password));
  const hasSymbol = computed(() => /[^a-zA-Z\d]/.test(props.password));

  // The only backend-enforced requirement.
  const meetsMinLength = computed(() => len.value >= props.minLength);

  // Strength score on a 1..4 scale (Fraca → Forte). Anything that fails the
  // hard minimum is pinned to "Fraca" so the bar never over-promises.
  const level = computed(() => {
    if (len.value === 0) return 0;
    if (!meetsMinLength.value) return 1;
    let raw = 1; // baseline for meeting the minimum
    if (len.value >= 12) raw += 1;
    if (hasLower.value && hasUpper.value) raw += 1;
    if (hasNumber.value) raw += 1;
    if (hasSymbol.value) raw += 1;
    // raw is 1..5 → clamp to 1..4
    return Math.min(4, raw <= 1 ? 1 : raw - 1);
  });

  const levelLabel = computed(() => {
    const labels: Record<number, string> = {
      1: t('web.auth.password_reset.strength.weak'),
      2: t('web.auth.password_reset.strength.fair'),
      3: t('web.auth.password_reset.strength.good'),
      4: t('web.auth.password_reset.strength.strong'),
    };
    return labels[level.value] ?? '';
  });

  // Segment/label colors. red-600 / amber-500 / emerald-500 / emerald-600 keep
  // a legible label in light mode; brighter variants for dark.
  const barColor = computed(() => {
    switch (level.value) {
      case 1:
        return 'bg-red-600 dark:bg-red-500';
      case 2:
        return 'bg-amber-500 dark:bg-amber-400';
      case 3:
        return 'bg-emerald-500 dark:bg-emerald-400';
      case 4:
        return 'bg-emerald-600 dark:bg-emerald-400';
      default:
        return 'bg-transparent';
    }
  });

  const labelColor = computed(() => {
    switch (level.value) {
      case 1:
        return 'text-red-600 dark:text-red-400';
      case 2:
        return 'text-amber-600 dark:text-amber-400';
      case 3:
        return 'text-emerald-600 dark:text-emerald-400';
      case 4:
        return 'text-emerald-700 dark:text-emerald-400';
      default:
        return 'text-day-ink-faint dark:text-night-ink-faint';
    }
  });

  // Short recommendations to strengthen the password (NOT requirements).
  const recommendations = computed(() => [
    { key: 'case', met: hasLower.value && hasUpper.value, label: t('web.auth.password_reset.strength.rec_case') },
    { key: 'number', met: hasNumber.value, label: t('web.auth.password_reset.strength.rec_number') },
    { key: 'symbol', met: hasSymbol.value, label: t('web.auth.password_reset.strength.rec_symbol') },
  ]);
</script>

<template>
  <div
    v-if="len > 0"
    class="mt-2 space-y-2 font-mono"
    data-testid="password-strength-meter"
    aria-live="polite">
    <!-- Segmented strength bar -->
    <div class="flex items-center gap-2">
      <div class="flex flex-1 gap-1" aria-hidden="true">
        <span
          v-for="seg in 4"
          :key="seg"
          class="strength-seg h-1 flex-1 rounded-full bg-day-line-2 dark:bg-white/[0.13]"
          :class="seg <= level ? barColor : ''"></span>
      </div>
      <span
        class="min-w-[3.5rem] text-right text-[10px] uppercase tracking-[0.08em]"
        :class="labelColor">
        {{ levelLabel }}
      </span>
    </div>

    <!-- Hard requirement (backend-enforced): minimum length -->
    <p
      class="flex items-center gap-1.5 text-[10px]"
      :class="meetsMinLength
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-day-ink-faint dark:text-night-ink-faint'"
      data-testid="password-req-minlength">
      <span aria-hidden="true">{{ meetsMinLength ? '✓' : '○' }}</span>
      <span>{{ t('web.auth.password_reset.strength.req_min_length', { n: props.minLength }) }}</span>
    </p>

    <!-- Recommendations to strengthen (not required) -->
    <div class="space-y-1">
      <p class="text-[10px] text-day-ink-faint dark:text-night-ink-faint">
        {{ t('web.auth.password_reset.strength.recommend_label') }}
      </p>
      <ul class="flex flex-wrap gap-x-3 gap-y-1">
        <li
          v-for="rec in recommendations"
          :key="rec.key"
          class="flex items-center gap-1 text-[10px]"
          :class="rec.met
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-day-ink-faint dark:text-night-ink-faint'">
          <span aria-hidden="true">{{ rec.met ? '✓' : '·' }}</span>
          <span>{{ rec.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
  .strength-seg {
    transition: background-color 200ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .strength-seg {
      transition: none;
    }
  }
</style>
